import { Box, Grid } from '@material-ui/core'
import React, { useRef } from 'react'
import { observer } from '~/common/mobx.decorator'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { FieldArray, Form, Formik } from 'formik'
import { FormMode } from '@opus/web.core.form.utils'
import { referenceTabStore } from './reference-tab.store'
import { Button } from '~/components/button'
import { ReferenceForm } from '~/features/care-activation/components/reference-form'
import * as yup from 'yup'
import validator from 'validator'
import { authStore } from '~/stores'
import { Typography } from '~/components/typography'
import { additionalInfoApplyDialogStore } from '~/components/additional-info-apply-dialog/additional-info-apply-dialog.store'
import { OnValueChange } from '~/components/form'

const validateSchema = yup.object({
	references: yup
		.array()
		.of(
			yup.object({
				firstName: yup.string().trim().required('Please enter first name').typeError('Please enter first name').max(60, 'Maximum is 60 characters'),
				lastName: yup.string().trim().required('Please enter last name').typeError('Please enter last name').max(60, 'Maximum is 60 characters'),
				email: yup.string().trim().email('Invalid email format, please recheck').required('Please enter email address'),
				phone: yup
					.string()
					.trim()
					.required('Please enter phone number')
					.typeError('Please enter phone number')
					.test('valid-phone-test', 'Invalid phone number format, please recheck', function (value) {
						if (!value) {
							return false
						}
						return validator.isMobilePhone(value)
					}),
				jobTitle: yup.string().trim().required('Please enter job title').typeError('Please enter job title').max(60, 'Maximum is 60 characters'),
				relationship: yup.string().trim().required('Please select reference relationship').typeError('Please select reference relationship'),
			})
		)
		.min(1, 'Please add at least one reference'),
})

const ReferenceViewForm = observer(({ initialValues = {}, onSubmit, onPreviousTab }) => {
	const { t } = useTranslation()
	const formRef = useRef()
	const { nullReferenceList } = referenceTabStore

	const { syncStepData } = additionalInfoApplyDialogStore

	return (
		<Formik innerRef={formRef} enableReinitialize={true} initialValues={{ references: initialValues }} onSubmit={onSubmit} validationSchema={validateSchema}>
			{({ handleSubmit, values, errors, isSubmitting, dirty }) => {
				return (
					<Form onSubmit={handleSubmit}>
						<OnValueChange onChange={({ values, dirty }) => syncStepData('workerReferences', values?.references, dirty)} />
						<FieldArray name="references">
							{({ push, remove }) => (
								<>
									{authStore.id ? (
										<Box>
											{values.references && values.references.length > 1
												? values?.references?.map((item, index) => (
														<Box key={item?.id}>
															<Typography variant="h3">
																{' '}
																{t('REFERENCE')} {index + 1}
															</Typography>
															<Box style={{ padding: 24, borderRadius: 8, margin: '10px 0', border: '1px solid #E6E6E6' }}>
																<ReferenceForm mode={FormMode.Edit} formName="references" index={index} />
															</Box>
														</Box>
												  ))
												: nullReferenceList?.map((item, index) => (
														<Box key={index}>
															<Typography variant="h3">
																{' '}
																{t('REFERENCE')} {index + 1}
															</Typography>
															<Box style={{ padding: 24, borderRadius: 8, margin: '10px 0', border: '1px solid #E6E6E6' }}>
																<ReferenceForm mode={FormMode.Edit} formName="references" index={index} />
															</Box>
														</Box>
												  ))}
										</Box>
									) : (
										<Box>
											<Typography variant="h3"> {t('REFERENCE')} 1</Typography>
											<Box style={{ padding: 24, borderRadius: 8, margin: '10px 0', border: '1px solid #E6E6E6' }}>
												<ReferenceForm mode={FormMode.Edit} formName="references" index={0} />
											</Box>
											<Typography variant="h3"> {t('REFERENCE')} 2</Typography>
											<Box style={{ padding: 24, borderRadius: 8, margin: '10px 0', border: '1px solid #E6E6E6' }}>
												<ReferenceForm mode={FormMode.Edit} formName="references" index={1} />
											</Box>
										</Box>
									)}
								</>
							)}
						</FieldArray>

						<Box mt={2}>
							<Grid container spacing={2}>
								<Grid item xs={12} md={12}>
									<Button loading={isSubmitting} disabled={isSubmitting} type="submit" fullWidth variant="contained" color="primary">
										{additionalInfoApplyDialogStore?.lookupSkillChecklistsByJob?.length === 0 ? t('COMPLETE') : t('NEXT_STEP')}
									</Button>
								</Grid>
							</Grid>
						</Box>
					</Form>
				)
			}}
		</Formik>
	)
})

const ReferenceSection = observer(({ onNextTab, onPreviousTab }) => {
	const { stepData, handleSubmitStepData } = additionalInfoApplyDialogStore

	const initialValues = stepData['workerReferences'] || []

	return (
		<>
			<Box>
				<ReferenceViewForm
					onSubmit={(values) => {
						handleSubmitStepData('workerReferences', values?.references)
					}}
					initialValues={initialValues}
					onPreviousTab={onPreviousTab}
				/>
			</Box>
		</>
	)
})

export const ReferenceTab = observer(({ hintText, onNextTab, onPreviousTab }) => {
	return (
		<Box p={2}>
			<ReferenceSection onNextTab={onNextTab} onPreviousTab={onPreviousTab} />
		</Box>
	)
})
